<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import { Statuses } from '@/V2/components/status/utils';
import {
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
  COMPLIANCE_RESULT_TO_STATUS,
} from '@/constants/businessComplianceAgent';
import LoadingBoxes from '@/V2/components/LoadingBoxes.vue';
import AiLabel from '@/V2/components/AiLabel.vue';

import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
} from '@inscribe/signal';
import CustomerEmptyState, {
  CustomerEmptyStateSize,
} from '@/V2/customers/CustomerEmptyState.vue';

export default {
  components: {
    ExpandableContainer,
    ExpandableContainerItem,
    StatusBar,
    LoadingBoxes,
    iButton,
    CustomerEmptyState,
    AiLabel,
  },
  props: {
    summary: {
      type: Object,
    },
    loading: {
      type: Boolean,
    },
    latestComplianceRun: {
      type: Object,
    },
    title: {
      type: String,
      default: 'Analysis',
    },
    buttonText: {
      type: String,
      default: null,
    },
    isComplianceRunning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      Statuses,
      CustomerEmptyStateSize,
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      COMPLIANCE_RESULT_TO_READABLE_LABEL,
      COMPLIANCE_RESULT_TO_STATUS,
    };
  },
  computed: {
    showLoadingState() {
      return this.loading || this.isComplianceRunning;
    },
    hasEmptyState() {
      return !this.latestComplianceRun?.output;
    },
  },
};
</script>

<template>
  <div
    class="customer-compliance-summary"
    data-testid="customer-compliance-summary"
  >
    <ExpandableContainer :title="title" :expandable="false">
      <template #tools>
        <i-button
          v-if="buttonText"
          :text="buttonText"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="(e) => $emit('button-click', e)"
        />
      </template>

      <template #items>
        <!-- loading state -->
        <div
          v-if="showLoadingState"
          class="summary slot-content d-flex flex-column i-p-9 i-gap-6"
          data-testid="customer-compliance-summary-loading"
        >
          <div class="d-flex flex-column i-gap-6 flex-grow-1">
            <status-bar
              :state="Statuses.LOADING"
              :label="isComplianceRunning ? 'Analyzing...' : 'Loading...'"
            />
            <loading-boxes
              :config="{ lengths: [540, 426, 467], repeat: 1 }"
            ></loading-boxes>
          </div>

          <ai-label text="AI Compliance Analyst" />
        </div>

        <!-- empty state -->
        <customer-empty-state
          v-else-if="hasEmptyState"
          class="summary slot-content"
          :title="'No Compliance Rating'"
          :description="
            isComplianceRunning
              ? 'AI Compliance Analyst running...'
              : 'AI Compliance Analyst not started'
          "
          icon="medium-user-check"
          :size="CustomerEmptyStateSize.SMALL"
          :show-button="false"
          data-testid="customer-fraud-summary-empty-state"
        />

        <div
          v-else
          class="summary slot-content d-flex flex-column space-between i-p-9 i-gap-6"
        >
          <status-bar
            v-if="latestComplianceRun.output?.result"
            :state="
              COMPLIANCE_RESULT_TO_STATUS[latestComplianceRun.output?.result]
            "
            :label="
              COMPLIANCE_RESULT_TO_READABLE_LABEL[
                latestComplianceRun.output?.result
              ]
            "
          />
          <div
            class="summary-analysis i-text-body-2 i-text-neutral-120 flex-grow-1"
          >
            {{ latestComplianceRun?.output?.analysis }}
          </div>

          <ai-label text="AI Compliance Analyst" />
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.summary {
  height: 100%;
  min-height: 196px;
  background: $white-base;
}
</style>
